import { Link, useForm } from "@inertiajs/react"
import type { FormEvent } from "react"

import AdminLayout from "../../../shared/AdminLayout"
import { csrfToken } from "../../../shared/csrf"

interface AdminContactFormLabels {
  nameLabel: string
  categoryLabel: string
  categoryHint: string
  userLabel: string
  userNone: string
  noteLabel: string
  optional: string
  phoneLabel: string
  phoneHint: string
  whatsappLabel: string
  whatsappHint: string
  telegramLabel: string
  telegramHint: string
  telegramPlaceholder: string
  save: string
  delete: string
  deleteConfirm: string
}

interface AdminContactFormProps {
  heading: string
  backHref: string
  backLabel: string
  // The collection route for a new contact (POST) or the contact's own
  // address (PATCH) -- distinct from `backHref`, the same
  // `AdminScheduleItemForm` shape.
  submitHref: string
  // `null` on the new-contact form -- absent, not merely hidden, is what
  // keeps «Удалить контакт» off that screen (FR-4.5) and also tells this
  // component which HTTP verb `submitHref` expects.
  deleteHref: string | null
  contact: {
    // `null` on the new-contact form; every value here is already the
    // model's own display form (FR-2.4) -- international for a
    // parseable number, verbatim for a short code, "@"-prefixed for
    // Telegram -- never the raw stored value.
    name: string | null
    category: string | null
    notes: string | null
    phone: string | null
    whatsapp: string | null
    telegram: string | null
    // FR-8.1: `null` means "external contact" -- the bare column, no
    // display transform of its own (unlike the channels above).
    userId: number | null
  }
  // FR-4.6: the `<datalist>` source, in Contacts::SuggestCategories's own
  // order -- distinct stored spellings, trimmed and verbatim.
  categoryOptions: string[]
  // FR-8.1, FR-8.4: every user, unfiltered, ordered by name -- a user
  // already linked elsewhere stays selectable (the collision surfaces on
  // save as FR-8.3's own uniqueness error, not hidden from the list).
  userOptions: { value: number; label: string }[]
  labels: AdminContactFormLabels
}

// The form's own local state stays flat, keyed by the model's bare
// attribute names (matching every other admin form's reasoning,
// AdminScheduleItemForm.tsx): the server's failed-save `errors` prop
// round-trips under these same bare keys, and the wire payload nests them
// under "contact" only at submit time (via `transform`).
interface ContactFormData {
  name: string
  category: string
  notes: string
  phone: string
  whatsapp: string
  telegram: string
  // A `<select>`'s value is always a string; the empty option's value is
  // "" (FR-8.1's "external contact"). Assigning an empty string to an
  // integer column casts to `nil` server-side (Rails' own blank-string
  // handling), so no client-side conversion is needed either way.
  user_id: string
  authenticity_token: string
}

const CATEGORY_LIST_ID = "contact-categories"

const INPUT_CLASSES = "h-11 rounded-[10px] border border-gray-300 px-3 text-[15px] text-gray-900"
const PHONE_INPUT_CLASSES = `${INPUT_CLASSES} tabular-nums`
const TELEGRAM_INPUT_CLASSES = `${INPUT_CLASSES} placeholder:text-gray-400`
const LABEL_TEXT_CLASSES = "text-[13px] font-medium leading-[18px] text-gray-700"
const OPTIONAL_LABEL_CLASSES = "flex items-baseline gap-2 text-[13px] font-medium leading-[18px] text-gray-700"
const HINT_CLASSES = "text-xs leading-4 text-gray-400"
const ERROR_CLASSES = "text-xs leading-4 text-red-700"

// The contact form (`Contacts::AdminContactsController#new/#edit`,
// docs/design/contacts.html's Block 4 verbatim): create and edit share
// this one component, the heading and the delete button's presence being
// the only differences (both server-computed, FR-4.5) -- the landed
// `AdminScheduleItemForm` shape. FR-4.10: no client-side format check
// runs before submit -- the server is the single source of validity, and
// every field's value is handed to `useForm` and back to the server
// untouched.
export default function AdminContactForm({
  heading, backHref, backLabel, submitHref, deleteHref, contact, categoryOptions, userOptions,
  labels,
}: AdminContactFormProps) {
  const form = useForm<ContactFormData>({
    name: contact.name ?? "",
    category: contact.category ?? "",
    notes: contact.notes ?? "",
    phone: contact.phone ?? "",
    whatsapp: contact.whatsapp ?? "",
    telegram: contact.telegram ?? "",
    user_id: contact.userId !== null ? String(contact.userId) : "",
    authenticity_token: csrfToken(),
  })

  form.transform((data) => ({
    contact: {
      name: data.name, category: data.category, notes: data.notes, user_id: data.user_id,
      phone: data.phone, whatsapp: data.whatsapp, telegram: data.telegram,
    },
    authenticity_token: data.authenticity_token,
  }))

  function handleSubmit(event: FormEvent) {
    event.preventDefault()
    if (deleteHref === null) {
      form.post(submitHref)
    } else {
      form.patch(submitHref)
    }
  }

  function handleDelete(href: string) {
    if (window.confirm(labels.deleteConfirm)) {
      form.delete(href)
    }
  }

  return (
    <AdminLayout>
      <Link href={backHref} className="flex items-center gap-1.5 text-sm text-accent">
        <svg aria-hidden="true" width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path
            d="M10 3.5L5.5 8l4.5 4.5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        {backLabel}
      </Link>
      <h2 className="text-[22px] font-bold leading-7 text-gray-900">{heading}</h2>
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <label className="flex flex-col gap-1.5">
          <span className={LABEL_TEXT_CLASSES}>{labels.nameLabel}</span>
          <input
            type="text"
            value={form.data.name}
            onChange={(event) => form.setData("name", event.target.value)}
            className={INPUT_CLASSES}
          />
          {form.errors.name && <span className={ERROR_CLASSES}>{form.errors.name}</span>}
        </label>
        <label className="flex flex-col gap-1.5">
          <span className={LABEL_TEXT_CLASSES}>{labels.categoryLabel}</span>
          <input
            type="text"
            list={CATEGORY_LIST_ID}
            value={form.data.category}
            onChange={(event) => form.setData("category", event.target.value)}
            className={INPUT_CLASSES}
          />
          <span className={HINT_CLASSES}>{labels.categoryHint}</span>
          {form.errors.category && <span className={ERROR_CLASSES}>{form.errors.category}</span>}
        </label>
        <datalist id={CATEGORY_LIST_ID}>
          {categoryOptions.map((category) => (
            <option key={category} value={category} />
          ))}
        </datalist>
        <label className="flex flex-col gap-1.5">
          <span className={LABEL_TEXT_CLASSES}>{labels.userLabel}</span>
          <select
            value={form.data.user_id}
            onChange={(event) => form.setData("user_id", event.target.value)}
            className={INPUT_CLASSES}
          >
            <option value="">{labels.userNone}</option>
            {userOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {form.errors.user_id && <span className={ERROR_CLASSES}>{form.errors.user_id}</span>}
        </label>
        <label className="flex flex-col gap-1.5">
          <span className={OPTIONAL_LABEL_CLASSES}>
            {labels.noteLabel}
            <span className="text-xs font-normal text-gray-400">{labels.optional}</span>
          </span>
          <input
            type="text"
            value={form.data.notes}
            onChange={(event) => form.setData("notes", event.target.value)}
            className={INPUT_CLASSES}
          />
        </label>
        <label className="flex flex-col gap-1.5">
          <span className={OPTIONAL_LABEL_CLASSES}>
            {labels.phoneLabel}
            <span className="text-xs font-normal text-gray-400">{labels.optional}</span>
          </span>
          <input
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            value={form.data.phone}
            onChange={(event) => form.setData("phone", event.target.value)}
            className={PHONE_INPUT_CLASSES}
          />
          <span className={HINT_CLASSES}>{labels.phoneHint}</span>
          {form.errors.phone && <span className={ERROR_CLASSES}>{form.errors.phone}</span>}
        </label>
        <label className="flex flex-col gap-1.5">
          <span className={OPTIONAL_LABEL_CLASSES}>
            {labels.whatsappLabel}
            <span className="text-xs font-normal text-gray-400">{labels.optional}</span>
          </span>
          <input
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            value={form.data.whatsapp}
            onChange={(event) => form.setData("whatsapp", event.target.value)}
            className={PHONE_INPUT_CLASSES}
          />
          <span className={HINT_CLASSES}>{labels.whatsappHint}</span>
          {form.errors.whatsapp && <span className={ERROR_CLASSES}>{form.errors.whatsapp}</span>}
        </label>
        <label className="flex flex-col gap-1.5">
          <span className={OPTIONAL_LABEL_CLASSES}>
            {labels.telegramLabel}
            <span className="text-xs font-normal text-gray-400">{labels.optional}</span>
          </span>
          <input
            type="text"
            placeholder={labels.telegramPlaceholder}
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            value={form.data.telegram}
            onChange={(event) => form.setData("telegram", event.target.value)}
            className={TELEGRAM_INPUT_CLASSES}
          />
          <span className={HINT_CLASSES}>{labels.telegramHint}</span>
          {form.errors.telegram && <span className={ERROR_CLASSES}>{form.errors.telegram}</span>}
        </label>
        <button
          type="submit"
          disabled={form.processing}
          className="mt-2 h-12 rounded-xl bg-accent text-[15px] font-semibold text-white disabled:opacity-60"
        >
          {labels.save}
        </button>
        {deleteHref !== null && (
          <button
            type="button"
            onClick={() => handleDelete(deleteHref)}
            className="flex items-center justify-center gap-1.5 py-1 text-sm font-medium text-red-700"
          >
            <svg aria-hidden="true" width="15" height="15" viewBox="0 0 16 16" fill="none">
              <path
                d="M2.5 4h11M6.5 4V2.5h3V4M4 4l.7 9a1.5 1.5 0 001.5 1.4h3.6A1.5 1.5 0 0011.3 13l.7-9M6.7 7v4.5M9.3 7v4.5"
                stroke="currentColor"
                strokeWidth="1.3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            {labels.delete}
          </button>
        )}
      </form>
    </AdminLayout>
  )
}
